import { escapeHtml } from '../util/escapeHtml.js';
import { anchored } from '../util/viewportAnchor.js';
import { mountShell } from '../components/Shell.js';
import { api, HttpError } from '../api/client.js';
import { store } from '../state/store.js';
import { showConfirmDialog } from '../components/ConfirmDialog.js';
import { attachItemInfoToggle } from '../components/ItemInfoPanel.js';
import { formatCrowns } from '../util/formatBig.js';
import {
  ITEM_CATEGORY_LABELS,
  ITEM_TIER_LABELS,
  type ItemCategory,
  type MarketListingDto,
} from '@rex-mortis/shared';

interface FlashState {
  kind: 'success' | 'danger' | 'warn';
  message: string;
}

interface FilterState {
  query: string;
  category: ItemCategory | '';
  maxPricePer: string;
}

const CATEGORY_OPTIONS: { value: ItemCategory | ''; label: string }[] = [
  { value: '', label: 'All categories' },
  { value: 'melee', label: ITEM_CATEGORY_LABELS.melee },
  { value: 'ranged', label: ITEM_CATEGORY_LABELS.ranged },
  { value: 'armor_chest', label: ITEM_CATEGORY_LABELS.armor_chest },
  { value: 'armor_head', label: ITEM_CATEGORY_LABELS.armor_head },
  { value: 'armor_shield', label: ITEM_CATEGORY_LABELS.armor_shield },
  { value: 'amulet', label: ITEM_CATEGORY_LABELS.amulet },
  { value: 'elixir', label: ITEM_CATEGORY_LABELS.elixir },
  { value: 'tonic', label: ITEM_CATEGORY_LABELS.tonic },
  { value: 'misc', label: ITEM_CATEGORY_LABELS.misc },
];

export async function renderMarket(root: HTMLElement): Promise<void> {
  let listings: MarketListingDto[] = [];
  let myListings: MarketListingDto[] = [];

  let filters: FilterState = {
    query: '',
    category: '',
    maxPricePer: '',
  };

  let flash: FlashState | null = null;

  // Initial load.
  try {
    const me = await api.me();
    store.set({ account: me.account, character: me.character });
    const r = await api.searchMarket();
    listings = r.listings;
    myListings = (await api.listOwnMarket()).listings;
  } catch {
    // tolerated
  }

  async function refreshSearch(): Promise<void> {
    try {
      const r = await api.searchMarket({
        query: filters.query || undefined,
        category: filters.category || undefined,
        maxPricePer: filters.maxPricePer || undefined,
      });
      listings = r.listings;
    } catch (err) {
      const msg = err instanceof HttpError ? err.message : 'Search failed.';
      flash = { kind: 'danger', message: msg };
    }
  }

  async function refreshMine(): Promise<void> {
    try {
      myListings = (await api.listOwnMarket()).listings;
    } catch {
      // tolerated
    }
  }

  mountShell(root, (main) => {
    const renderMain = () => {
      const char = store.get().character;
      if (!char) {
        main.innerHTML = '<p>Loading…</p>';
        return;
      }

      main.innerHTML = `
        <img class="page-banner" src="/img/banners/market.webp" alt="" />
        <h1>The Merchant's Market</h1>
        <p style="font-style: italic; color: var(--c-ink-soft); margin-top: -8px;">
          Where most fortunes are made. And lost.
        </p>

        <div id="flash"></div>

        <section class="panel">
          <h2>Browse</h2>
          <div class="filters">
            <div class="field">
              <label>Item name</label>
              <input type="text" id="f-name" placeholder="e.g. Misericorde" value="${escapeHtml(filters.query)}" />
            </div>
            <div class="field">
              <label>Category</label>
              <select id="f-cat">
                ${CATEGORY_OPTIONS.map(
                  (c) => `<option value="${c.value}" ${filters.category === c.value ? 'selected' : ''}>${c.label}</option>`,
                ).join('')}
              </select>
            </div>
            <div class="field">
              <label>Max price/each</label>
              <input type="text" id="f-max" placeholder="e.g. 1000" value="${escapeHtml(filters.maxPricePer)}" />
            </div>
            <button class="btn ghost" id="f-apply">Apply filters</button>
            <button class="btn ghost" id="f-clear">Clear</button>
          </div>
          <div id="results"></div>
        </section>

        <section class="panel">
          <h2>My active listings</h2>
          <div id="mine"></div>
        </section>
      `;

      const style = document.createElement('style');
      style.textContent = `
        .filters {
          display: flex;
          flex-wrap: wrap;
          gap: var(--s-3);
          align-items: end;
          margin-bottom: var(--s-3);
        }
        .filters .field { margin-bottom: 0; flex: 1 1 160px; min-width: 140px; }
        .filters select, .filters input {
          font: inherit;
          padding: var(--s-2) var(--s-3);
          /* Colours (incl. placeholder) come from the global pinned cream cell
             in styles/pages.css — var(--c-parchment) here went dark-on-dark in
             dark mode. */
          border: 1px solid var(--c-rule);
          border-radius: var(--radius);
        }
        .market-table {
          width: 100%;
          border-collapse: collapse;
          font-family: var(--font-numeric);
        }
        .market-table th, .market-table td {
          text-align: left;
          padding: 6px 10px;
          border-bottom: 1px solid var(--c-rule-soft);
          font-size: 0.92rem;
        }
        .market-table th {
          font-family: var(--font-display);
          color: var(--c-ink-soft);
          background: var(--c-parchment-dark);
        }
        .market-table .qty-input {
          width: 60px;
          padding: 2px 6px;
          font: inherit;
          border: 1px solid var(--c-rule);
          border-radius: var(--radius);
          /* Background + text colour are owned by the GLOBAL pinned cream cell in
             pages.css (cream bg + dark ink, both themes). Declaring them here with
             surface tokens flips them in dark mode -> dark-on-dark. Do not re-add. */
        }
        .market-table .btn { padding: 4px 10px; font-size: 0.85rem; }
        /* Keep the qty input + Buy/Yours button on one line and give the button
           a uniform width so "Yours" sits exactly where "Buy" does (no wrap-
           below, so the column flows evenly down the page). */
        .market-table td[data-label="Buy"] { white-space: nowrap; }
        .market-table td[data-label="Buy"] .qty-input { vertical-align: middle; margin-right: 6px; }
        .market-table td[data-label="Buy"] .btn { vertical-align: middle; min-width: 64px; text-align: center; }
        .empty {
          font-style: italic;
          color: var(--c-ink-soft);
          padding: var(--s-3);
        }
        /* Mobile: 7 columns can't fit a phone width — the header wrapped and the
           columns drifted out of alignment. Below 600px each row becomes a
           labelled card (label on the left via data-label, value on the right). */
        @media (max-width: 600px) {
          .market-table thead { display: none; }
          .market-table, .market-table tbody, .market-table tr, .market-table td {
            display: block;
            width: 100%;
          }
          .market-table tr {
            border: 1px solid var(--c-rule);
            border-radius: var(--radius);
            background: var(--c-parchment);
            margin-bottom: var(--s-3);
            padding: var(--s-1) var(--s-2);
          }
          .market-table td {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: var(--s-3);
            text-align: right;
            padding: 8px 4px;
            border-bottom: 1px solid var(--c-rule-soft);
            font-size: 0.95rem;
          }
          .market-table td:last-child { border-bottom: 0; }
          .market-table td::before {
            content: attr(data-label);
            flex: 0 0 auto;
            text-align: left;
            font-family: var(--font-display);
            color: var(--c-ink-soft);
            font-size: 0.85rem;
          }
          /* The Buy cell holds a qty input + button; keep them grouped on the right. */
          .market-table td[data-label="Buy"] { flex-wrap: wrap; justify-content: flex-end; }
          .market-table .qty-input { width: 64px; }
        }
      `;
      main.appendChild(style);

      // Flash.
      const flashRoot = main.querySelector<HTMLDivElement>('#flash')!;
      if (flash) {
        const div = document.createElement('div');
        div.className = `alert ${flash.kind === 'warn' ? 'danger' : flash.kind}`;
        div.textContent = flash.message;
        flashRoot.appendChild(div);
      }

      // Filter buttons.
      main.querySelector<HTMLButtonElement>('#f-apply')?.addEventListener('click', async () => {
        filters = {
          query: (main.querySelector<HTMLInputElement>('#f-name')?.value ?? '').trim(),
          category: (main.querySelector<HTMLSelectElement>('#f-cat')?.value ?? '') as
            | ItemCategory
            | '',
          maxPricePer: (main.querySelector<HTMLInputElement>('#f-max')?.value ?? '').trim(),
        };
        await refreshSearch();
        anchored(renderMain);
      });
      main.querySelector<HTMLButtonElement>('#f-clear')?.addEventListener('click', async () => {
        filters = { query: '', category: '', maxPricePer: '' };
        await refreshSearch();
        anchored(renderMain);
      });

      // Render results table.
      const results = main.querySelector<HTMLDivElement>('#results')!;
      if (listings.length === 0) {
        const hasFilters =
          filters.query.trim() !== '' ||
          filters.category !== '' ||
          filters.maxPricePer.trim() !== '';
        results.innerHTML = hasFilters
          ? `<p class="empty">No listings match. Try widening the filters.</p>`
          : `<p class="empty">The market is quiet today. List something from your Inventory to break the silence.</p>`;
      } else {
        const table = document.createElement('table');
        table.className = 'market-table';
        table.innerHTML = `
          <thead>
            <tr>
              <th>Item</th>
              <th>Tier</th>
              <th>Available</th>
              <th>Price each</th>
              <th>Buy now total</th>
              <th>Seller</th>
              <th>Buy</th>
            </tr>
          </thead>
          <tbody>
            ${listings
              .map(
                (l) => `
              <tr>
                <td data-label="Item"><span class="mk-item-name" data-slug="${escapeHtml(l.itemSlug)}">${escapeHtml(l.itemName)}</span></td>
                <td data-label="Tier">${ITEM_TIER_LABELS[l.itemTier as keyof typeof ITEM_TIER_LABELS] ?? l.itemTier}</td>
                <td data-label="Available">${l.qty} / ${l.qtyOriginal}</td>
                <td data-label="Price each">${formatCrowns(l.pricePer)}</td>
                <td data-label="Buy now total">${formatCrowns(l.totalIfBuyAll)}</td>
                <td data-label="Seller">${escapeHtml(l.sellerName)}</td>
                <td data-label="Buy">
                  <input class="qty-input" type="number" min="1" max="${l.qty}" value="1" data-buy-qty="${l.id}" />
                  <button class="btn" data-buy="${l.id}" ${l.sellerId === char.id ? 'disabled' : ''}>
                    ${l.sellerId === char.id ? 'Yours' : 'Buy'}
                  </button>
                </td>
              </tr>`,
              )
              .join('')}
          </tbody>
        `;
        results.appendChild(table);

        // Make item names clickable — opens the shared item-detail modal, just
        // like in the Inventory, so buyers can inspect stats before purchasing.
        results.querySelectorAll<HTMLElement>('.mk-item-name[data-slug]').forEach((el) => {
          attachItemInfoToggle(el, el.dataset.slug!);
        });

        // Wire Buy buttons.
        results.querySelectorAll<HTMLButtonElement>('button[data-buy]').forEach((btn) => {
          btn.addEventListener('click', async () => {
            const id = btn.dataset.buy!;
            const qtyInput = results.querySelector<HTMLInputElement>(`input[data-buy-qty="${id}"]`);
            const qty = Math.max(1, Math.floor(Number(qtyInput?.value ?? '1')));
            results.querySelectorAll<HTMLButtonElement>('button[data-buy]').forEach((b) => (b.disabled = true));
            try {
              const r = await api.buyOnMarket({ listingId: id, qty });
              store.set({ character: r.character });
              flash = {
                kind: 'success',
                message: `Bought ${r.qty}× ${r.itemName} for ${formatCrowns(r.totalPaid)} crowns. ${r.listingRemaining} left at this price.`,
              };
              await refreshSearch();
            } catch (err) {
              const msg = err instanceof HttpError ? err.message : 'Buy failed.';
              flash = { kind: 'danger', message: msg };
            } finally {
              anchored(renderMain);
            }
          });
        });
      }

      // My listings.
      const mine = main.querySelector<HTMLDivElement>('#mine')!;
      if (myListings.length === 0) {
        mine.innerHTML = `<p class="empty">You have no active listings. List items from your Inventory.</p>`;
      } else {
        const table = document.createElement('table');
        table.className = 'market-table';
        table.innerHTML = `
          <thead>
            <tr>
              <th>Item</th>
              <th>Remaining</th>
              <th>Price each</th>
              <th>Expires</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            ${myListings
              .map(
                (l) => `
              <tr>
                <td data-label="Item"><span class="mk-item-name" data-slug="${escapeHtml(l.itemSlug)}">${escapeHtml(l.itemName)}</span></td>
                <td data-label="Remaining">${l.qty} / ${l.qtyOriginal}</td>
                <td data-label="Price each">${formatCrowns(l.pricePer)}</td>
                <td data-label="Expires">${new Date(l.expiresAt).toLocaleString()}</td>
                <td data-label="Action"><button class="btn ghost" data-cancel="${l.id}">Cancel</button></td>
              </tr>`,
              )
              .join('')}
          </tbody>
        `;
        mine.appendChild(table);

        // Clickable item names here too.
        mine.querySelectorAll<HTMLElement>('.mk-item-name[data-slug]').forEach((el) => {
          attachItemInfoToggle(el, el.dataset.slug!);
        });

        mine.querySelectorAll<HTMLButtonElement>('button[data-cancel]').forEach((btn) => {
          btn.addEventListener('click', async () => {
            const id = btn.dataset.cancel!;
            if (!(await showConfirmDialog({
              title: 'Cancel this listing?',
              message: 'Remaining items return to your inventory. The listing fee is non-refundable.',
              confirmLabel: 'Cancel Listing',
            }))) return;
            mine.querySelectorAll<HTMLButtonElement>('button[data-cancel]').forEach((b) => (b.disabled = true));
            try {
              const r = await api.cancelMarketListing({ listingId: id });
              store.set({ character: r.character });
              flash = { kind: 'success', message: 'Listing cancelled. Items returned to inventory.' };
              await refreshMine();
            } catch (err) {
              const msg = err instanceof HttpError ? err.message : 'Cancel failed.';
              flash = { kind: 'danger', message: msg };
            } finally {
              anchored(renderMain);
            }
          });
        });
      }
    };

    anchored(renderMain);
  });
}

